import { Person } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useVacationBalancesReportData,
} from '~/data/reports';
import {
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';
import { ReportColumnsVisibility } from '~/scenes/PortalModules/Analytics/common/ReportColumnsVisibility';
import { ReportDataExport } from '~/scenes/PortalModules/Analytics/common/ReportDataExport';
import { ReportViewLayout } from '~/scenes/PortalModules/Analytics/common/ReportViewLayout';

import { useVacationBalancesReport } from './useVacationBalancesReport';

export const VacationBalancesReport = () => {
  const { t } = useTranslation();

  const apiRef = useGridApiRef();
  const loggedInEmployeeId = useLoggedInEmployeeId();

  const defaultReportSelection = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
      employeeId: loggedInEmployeeId,
      scope: AnalyticsReportScope.Direct,
    },
    params: {
      empScope: AnalyticsReportEmployeeScope.AllEmployees,
    },
  };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        roles: [AnalyticsReportRoleScope.Manager],
      },
    },
  });

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useVacationBalancesReportData(reportDatasetProps.reportSelection);

  const { table } = useVacationBalancesReport({
    data,
  });

  return (
    <ReportViewLayout
      heading={t('laVacationBalancesReport')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout showHeading={true}>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoadingReportData}
                failedToLoad={failedToLoad}
              />
              <ReportDatasetRowCount
                count={table.rows.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility
                apiRef={apiRef}
                columnGroupingModel={table.columnGroupingModel}
                isLoading={isLoadingReportData}
              />
              <ReportDataExport
                title={t('laVacationBalancesReport')}
                apiRef={apiRef}
                isLoading={isLoadingReportData}
              />
            </>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={table.columnConfigs}
        rows={table.rows}
        columnGroupingModel={table.columnGroupingModel}
        initialState={{ density: 'compact' }}
        getRowId={(row) => row.rowId}
        pinnedColumns={{
          left: ['employeeId', 'firstName', 'lastName'],
        }}
      />
    </ReportViewLayout>
  );
};
